// カラーパレット定義
export type ColorPalette = {
  name: string
  description: string
  colors: {
    // Primary colors
    primary: string
    primaryLight: string
    primaryDark: string
    
    // Secondary colors
    secondary: string
    secondaryLight: string
    secondaryDark: string
    
    // Accent colors
    accent: string
    accentLight: string
    accentDark: string
    
    // Base colors
    background: string
    surface: string
    surfaceLight: string
    surfaceDark: string  // 追加: 暗い背景用
    
    // Text colors
    textPrimary: string
    textSecondary: string
    textInverse: string
    
    // UI colors
    success: string
    successDark: string
    successLight: string
    warning: string
    warningDark: string
    warningLight: string
    error: string
    errorDark: string
    errorLight: string
    info: string
    infoDark: string
    infoLight: string
    
    // Border colors
    border: string
    borderLight: string
    borderStrong: string
    
    // Grade colors (固定)
    gradeA: string
    gradeB: string
    gradeC: string
    gradeD: string
  }
}

// 1. フォレスト＆ネイビー（堅実・信頼）- 金融、法律、コンサルティング向け
export const forestNavyPalette: ColorPalette = {
  name: 'Forest & Navy',
  description: '堅実・信頼 - 金融、法律、コンサルティング向け',
  colors: {
    primary: '#1E4D3D',
    primaryLight: '#2A6B54',
    primaryDark: '#14352A',
    
    secondary: '#1B365D',
    secondaryLight: '#274A7A',
    secondaryDark: '#122540',
    
    accent: '#B8860B',
    accentLight: '#D4A017',
    accentDark: '#9B7209',
    
    background: '#F5F5F0',
    surface: '#FFFFFF',
    surfaceLight: '#FAFAF7',
    surfaceDark: '#14352A',
    
    textPrimary: '#1A1A1A',
    textSecondary: '#5A5A5A',
    textInverse: '#FFFFFF',
    
    success: '#1E4D3D',
    successDark: '#14352A',
    successLight: '#2A6B54',
    warning: '#B8860B',
    warningDark: '#9B7209',
    warningLight: '#D4A017',
    error: '#C62828',
    errorDark: '#8E1E1E',
    errorLight: '#EF9A9A',
    info: '#1B365D',
    infoDark: '#122540',
    infoLight: '#274A7A',
    
    border: '#E0E0DC',
    borderLight: '#F0F0EC',
    borderStrong: '#C0C0BC',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 2. セージ＆チャコール（落ち着き・誠実）- 医療、ウェルネス、環境系向け
export const sageCharcoalPalette: ColorPalette = {
  name: 'Sage & Charcoal',
  description: '落ち着き・誠実 - 医療、ウェルネス、環境系向け',
  colors: {
    primary: '#6B8E6B',
    primaryLight: '#84A584',
    primaryDark: '#527052',
    
    secondary: '#36454F',
    secondaryLight: '#4A5D6A',
    secondaryDark: '#242F37',
    
    accent: '#C4B7A6',
    accentLight: '#D6CCBE',
    accentDark: '#B2A294',
    
    background: '#FAFAF8',
    surface: '#FFFFFF',
    surfaceLight: '#FCFCFB',
    surfaceDark: '#242F37',
    
    textPrimary: '#2A2A2A',
    textSecondary: '#6A6A6A',
    textInverse: '#FFFFFF',
    
    success: '#6B8E6B',
    successDark: '#527052',
    successLight: '#84A584',
    warning: '#D4A574',
    warningDark: '#B88D5C',
    warningLight: '#E0B88C',
    error: '#B85450',
    errorDark: '#9C3E3A',
    errorLight: '#D06A66',
    info: '#5A7A8A',
    infoDark: '#425D6B',
    infoLight: '#7297A9',
    
    border: '#E8E8E4',
    borderLight: '#F2F2EE',
    borderStrong: '#D0D0CC',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 3. ティール＆スレートグレー（革新・安定）- テック系、スタートアップ向け
export const tealSlatePalette: ColorPalette = {
  name: 'Teal & Slate',
  description: '革新・安定 - テック系、スタートアップ向け',
  colors: {
    primary: '#2A6B6B',
    primaryLight: '#3A8484',
    primaryDark: '#1F4F4F',
    
    secondary: '#5A6872',
    secondaryLight: '#6F7E8A',
    secondaryDark: '#454F58',
    
    accent: '#E07A5F',
    accentLight: '#E89479',
    accentDark: '#D66045',
    
    background: '#F0F2F4',
    surface: '#FFFFFF',
    surfaceLight: '#F7F8FA',
    surfaceDark: '#1F4F4F',
    
    textPrimary: '#1F2937',
    textSecondary: '#6B7280',
    textInverse: '#FFFFFF',
    
    success: '#2A6B6B',
    successDark: '#1F4F4F',
    successLight: '#3A8484',
    warning: '#F59E0B',
    warningDark: '#D97706',
    warningLight: '#FCD34D',
    error: '#E07A5F',
    errorDark: '#D66045',
    errorLight: '#E89479',
    info: '#3B82C4',
    infoDark: '#2563EB',
    infoLight: '#60A5FA',
    
    border: '#E5E7EB',
    borderLight: '#F3F4F6',
    borderStrong: '#D1D5DB',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 4. エバーグリーン＆アイボリー（自然・温かみ）- 教育、農業、サステナビリティ向け
export const evergreenIvoryPalette: ColorPalette = {
  name: 'Evergreen & Ivory',
  description: '自然・温かみ - 教育、農業、サステナビリティ向け',
  colors: {
    primary: '#355E3B',
    primaryLight: '#47784F',
    primaryDark: '#254429',
    
    secondary: '#5C4033',
    secondaryLight: '#745346',
    secondaryDark: '#442E25',
    
    accent: '#C96A4B',
    accentLight: '#D68466',
    accentDark: '#B65438',
    
    background: '#FFFFF0',
    surface: '#FFFFFF',
    surfaceLight: '#FFFFF7',
    surfaceDark: '#254429',
    
    textPrimary: '#2A2A2A',
    textSecondary: '#5A5A5A',
    textInverse: '#FFFFFF',
    
    success: '#355E3B',
    successDark: '#254429',
    successLight: '#47784F',
    warning: '#C96A4B',
    warningDark: '#B65438',
    warningLight: '#D68466',
    error: '#B85450',
    errorDark: '#9C3E3A',
    errorLight: '#D06A66',
    info: '#4A7C83',
    infoDark: '#366168',
    infoLight: '#5E9BA2',
    
    border: '#EAE8D8',
    borderLight: '#F5F3E3',
    borderStrong: '#D5D3C3',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 現在のEvaloidで使用されているカラー（参考用）
export const currentEvaloidPalette: ColorPalette = {
  name: 'Current Evaloid',
  description: '現在のEvaloidカラー',
  colors: {
    primary: '#047857',
    primaryLight: '#059669',
    primaryDark: '#03614A',
    
    secondary: '#14b8a6',
    secondaryLight: '#2dd4bf',
    secondaryDark: '#0f766e',
    
    accent: '#fbbf24',
    accentLight: '#fcd34d',
    accentDark: '#f59e0b',
    
    background: '#ffffff',
    surface: '#ffffff',
    surfaceLight: '#f9fafb',
    surfaceDark: '#03614A',
    
    textPrimary: '#111827',
    textSecondary: '#6b7280',
    textInverse: '#ffffff',
    
    success: '#059669',
    successDark: '#047857',
    successLight: '#10B981',
    warning: '#f59e0b',
    warningDark: '#d97706',
    warningLight: '#fcd34d',
    error: '#dc2626',
    errorDark: '#b91c1c',
    errorLight: '#ef4444',
    info: '#2563eb',
    infoDark: '#1d4ed8',
    infoLight: '#60A5FA',
    
    border: '#e5e7eb',
    borderLight: '#f3f4f6',
    borderStrong: '#d1d5db',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 5. クリーン＆トラスト（SaaS、フィンテック、ダッシュボード向け）
export const cleanTrustPalette: ColorPalette = {
  name: 'Clean & Trust',
  description: 'SaaS、フィンテック、ダッシュボード向け',
  colors: {
    primary: '#059669',
    primaryLight: '#D1FAE5',
    primaryDark: '#047857',
    
    secondary: '#475569',
    secondaryLight: '#64748B',
    secondaryDark: '#334155',
    
    accent: '#3B82F6',
    accentLight: '#60A5FA',
    accentDark: '#2563EB',
    
    background: '#F8FAFC',
    surface: '#FFFFFF',
    surfaceLight: '#FFFFFF',
    surfaceDark: '#1E293B',
    
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textInverse: '#FFFFFF',
    
    success: '#22C55E',
    successDark: '#16A34A',
    successLight: '#4ADE80',
    warning: '#FACC15',
    warningDark: '#F59E0B',
    warningLight: '#FDE047',
    error: '#EF4444',
    errorDark: '#DC2626',
    errorLight: '#F87171',
    info: '#059669',
    infoDark: '#047857',
    infoLight: '#10B981',
    
    border: '#E2E8F0',
    borderLight: '#F1F5F9',
    borderStrong: '#CBD5E1',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 6. ソフト＆プロフェッショナル（ヘルスケア、教育、BtoB向け）
export const softProfessionalPalette: ColorPalette = {
  name: 'Soft & Professional',
  description: 'ヘルスケア、教育、BtoB向け',
  colors: {
    primary: '#0D9488',
    primaryLight: '#CCFBF1',
    primaryDark: '#0F766E',
    
    secondary: '#64748B',
    secondaryLight: '#94A3B8',
    secondaryDark: '#475569',
    
    accent: '#F59E0B',
    accentLight: '#FCD34D',
    accentDark: '#D97706',
    
    background: '#F1F5F9',
    surface: '#FFFFFF',
    surfaceLight: '#FFFFFF',
    surfaceDark: '#1E293B',
    
    textPrimary: '#0F172A',
    textSecondary: '#64748B',
    textInverse: '#FFFFFF',
    
    success: '#22C55E',
    successDark: '#16A34A',
    successLight: '#4ADE80',
    warning: '#FACC15',
    warningDark: '#F59E0B',
    warningLight: '#FDE047',
    error: '#EF4444',
    errorDark: '#DC2626',
    errorLight: '#F87171',
    info: '#0D9488',
    infoDark: '#0F766E',
    infoLight: '#2DD4BF',
    
    border: '#E2E8F0',
    borderLight: '#F1F5F9',
    borderStrong: '#CBD5E1',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// 7. ナチュラル＆モダン（サステナビリティ、ウェルネスアプリ向け）
export const naturalModernPalette: ColorPalette = {
  name: 'Natural & Modern',
  description: 'サステナビリティ、ウェルネスアプリ向け',
  colors: {
    primary: '#16A34A',
    primaryLight: '#DCFCE7',
    primaryDark: '#15803D',
    
    secondary: '#57534E',
    secondaryLight: '#78716C',
    secondaryDark: '#44403C',
    
    accent: '#F87171',
    accentLight: '#FCA5A5',
    accentDark: '#EF4444',
    
    background: '#FAFAF9',
    surface: '#FFFFFF',
    surfaceLight: '#FFFFFF',
    surfaceDark: '#1C1917',
    
    textPrimary: '#1C1917',
    textSecondary: '#57534E',
    textInverse: '#FFFFFF',
    
    success: '#22C55E',
    successDark: '#16A34A',
    successLight: '#4ADE80',
    warning: '#FACC15',
    warningDark: '#F59E0B',
    warningLight: '#FDE047',
    error: '#EF4444',
    errorDark: '#DC2626',
    errorLight: '#F87171',
    info: '#16A34A',
    infoDark: '#15803D',
    infoLight: '#4ADE80',
    
    border: '#E7E5E4',
    borderLight: '#F5F5F4',
    borderStrong: '#D6D3D1',
    
    gradeA: '#10B981',
    gradeB: '#3B82F6',
    gradeC: '#F59E0B',
    gradeD: '#EF4444'
  }
}

// パレット一覧
export const colorPalettes = {
  forestNavy: forestNavyPalette,
  sageCharcoal: sageCharcoalPalette,
  tealSlate: tealSlatePalette,
  evergreenIvory: evergreenIvoryPalette,
  cleanTrust: cleanTrustPalette,
  softProfessional: softProfessionalPalette,
  naturalModern: naturalModernPalette,
  currentEvaloid: currentEvaloidPalette
}

// デフォルトパレット
export const defaultPalette = evergreenIvoryPalette

// パレット選択用のキー型
export type ColorPaletteKey = keyof typeof colorPalettes